"use client";

import { FeedAdCampaignData, ShowsObject } from "./types";
import { FeedEvents, trackEvent } from "@/analytics";
import { useEffect, useMemo, useState } from "react";

import AdUnitCarousel from "./AdUnitCarousel";
import { ArrowUpRight } from "lucide-react";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import Link from "next/link";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "dayjs";
import { getFeedAdObject } from "@/app/(public)/api/feed-items/utils/helpers";
import { getUserLocationOrFallbackToIp } from "@/data/zipcode";
import { logException } from "@/analytics/sentry";

interface Props {
  filteredComedianDictionary: { [comedianId: string]: Comedian };
}

export default function FeedAdUnit({ filteredComedianDictionary }: Props) {
  const [advertisement, setAdvertisement] = useState<FeedAdCampaignData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const supabase = createClientComponentClient<Database>();

  // get user location and fetch ad object on mount
  useEffect(() => {
    async function fetchLocationAndAdObject() {
      setLoading(true);

      // get user location from IP address
      const { latitude, longitude } =
        await getUserLocationOrFallbackToIp(supabase);

      let feedAdObject = null;
      if (!latitude && !longitude) {
        logException(
          "Latitude and longitude not found for user inside Feed page"
        );
      } else if (!latitude) {
        logException("Latitude not found for user inside Feed page");
      } else if (!longitude) {
        logException("Longitude not found for user inside Feed page");
      } else {
        feedAdObject = await getFeedAdObject({
          supabase,
          latitude,
          longitude,
          filteredComedianDictionary,
        });

        setAdvertisement(feedAdObject);
        trackEvent(FeedEvents.LOADED_FEED_AD_UNIT, {
          comedianName: feedAdObject?.comedianDisplayName,
          comedianPageUrl: feedAdObject?.comedianPageUrl,
          locationName: feedAdObject?.locationName,
          shows: feedAdObject?.shows,
        });
      }
      setLoading(false);
    }

    fetchLocationAndAdObject();
  }, [filteredComedianDictionary, supabase]);

  const slides = useMemo(() => {
    if (!advertisement) return [];

    const createdSlides = advertisement.shows
      .map((show) => ({
        id: show.id,
        Component: (
          <div className="min-w-0 flex-grow-0 flex-shrink-0 basis-[250px]">
            <AdUnitShowCard
              show={show}
              comedianDisplayName={advertisement.comedianDisplayName}
            />
          </div>
        ),
      }))
      .filter((slide) => slide !== null);
    return createdSlides;
  }, [advertisement]);

  // render nothing, because most users will not see an advertisement
  if (loading || !advertisement) return null;

  return (
    <div className="px-4 md:px-0">
      <div className="flex flex-col gap-4 pb-4 mb-4 border-b md:pb-6 md:mb-6 border-neutral-200">
        <div className="flex flex-col md:flex-row gap-3 md:gap-0 bg-[#29323C] rounded-md py-4 md:pl-3 md:pr-0 md:py-7">
          <div className="flex flex-row items-start justify-between gap-2 px-4 md:flex-col md:w-72">
            <div>
              <div className="flex flex-row gap-3">
                <Link
                  href={advertisement.comedianPageUrl}
                  onClick={() =>
                    trackEvent(FeedEvents.CLICKED_COMEDIAN_NAME_FEED_AD_UNIT, {
                      comedianName: advertisement.comedianDisplayName,
                      url: advertisement.comedianPageUrl,
                    })
                  }
                  className="hover:underline hover:underline-offset-2 duration-200 ease-in-out decoration-[transparent] hover:decoration-white transition-[text-decoration]"
                >
                  <p className="uppercase text-white font-anton font-medium text-2xl md:text-[26px] line-clamp-1 leading-normal">
                    {advertisement.comedianDisplayName}
                  </p>
                </Link>
              </div>
              <p className="text-[14px] font-regular uppercase font-medium text-white">
                {advertisement.locationName}
              </p>
            </div>
            <div className="mt-0.5 md:mt-0 flex flex-row items-center justify-start gap-1 px-2 py-1.5 text-[11px] md:text-xs rounded-md text-white bg-[#2044AB] select-none">
              <p>Sponsored Event</p>
            </div>
          </div>
          <AdUnitCarousel slides={slides} />
        </div>
      </div>
    </div>
  );
}

function AdUnitShowCard({
  show,
  comedianDisplayName,
}: {
  show: ShowsObject;
  comedianDisplayName: string | null;
}) {
  // format time to "8:00 PM"
  const formatTime = (showDate: string | null) =>
    dayjs.utc(showDate).format("h:mm A");

  // format date to "Monday, June 10"
  const formatDate = (showDate: string | null) =>
    dayjs.utc(showDate).format("dddd, MMMM D");

  return (
    <Link
      href={show.ticketLink}
      target="_blank"
      onClick={() =>
        trackEvent(FeedEvents.CLICKED_SHOW_AD_UNIT, {
          comedianName: comedianDisplayName,
          showId: show.id,
          showDatetime: show.datetimeOriginal,
          showTicketLink: show.ticketLink,
          showVenueId: show.venueId,
          target: "card",
        })
      }
      className="w-full min-w-[250px] text-[12px] flex items-start flex-col gap-1.5 p-3 bg-white border border-[#E1E1E1] rounded-md hover:bg-neutral-100 transition-colors duration-200 ease-in-out"
    >
      <p className="font-bold leading-tight uppercase">{show.venue}</p>
      <div className="flex flex-row gap-1 font-normal text-[#29323C] leading-tight">
        <p>{formatDate(show.datetimeOriginal)}</p>
        <p>|</p>
        <p>{formatTime(show.datetimeOriginal)}</p>
      </div>
      <button
        onClick={() =>
          trackEvent(FeedEvents.CLICKED_SHOW_AD_UNIT, {
            comedianName: comedianDisplayName,
            showId: show.id,
            showDatetime: show.datetimeOriginal,
            showTicketLink: show.ticketLink,
            showVenueId: show.venueId,
            target: "buy tickets",
          })
        }
        className="flex flex-row items-center gap-0.5 whitespace-nowrap text-white px-2.5 py-1.5 rounded-md bg-primary font-semibold text-[13px] mt-1"
      >
        Buy tickets <ArrowUpRight width={15} height={15} strokeWidth={2} />
      </button>
    </Link>
  );
}
